import { useState } from 'react';
import { ExternalLink, Star, X, Smartphone, Check, Coins } from 'lucide-react';
import { QRCodeSVG } from 'qrcode.react';
import type { DisplayGame, DisplayOffer } from '@swipeopinion/shared';
import { PlatformTag } from './PlatformTag';

/** True when the current user agent is a phone/tablet, so a direct store link works. */
export function isMobileUserAgent(): boolean {
  if (typeof navigator === 'undefined') return false;
  return /iphone|ipad|ipod|android/i.test(navigator.userAgent);
}

export function StoreQRModal({ url, title, onClose }: { url: string; title: string; onClose: () => void }) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-sm p-6 space-y-4" onClick={(e) => e.stopPropagation()} data-testid="store-qr-modal">
        <div className="flex items-start justify-between gap-4">
          <div className="flex items-center gap-2">
            <Smartphone className="w-5 h-5 text-primary" />
            <h3 className="text-lg font-bold font-display text-[#111827]">Install on your phone</h3>
          </div>
          <button onClick={onClose} aria-label="Close" className="text-[#6B7280] hover:text-[#111827]">
            <X className="w-5 h-5" />
          </button>
        </div>
        <p className="text-sm text-[#6B7280]">
          Scan this QR code with your phone to install <span className="font-medium text-[#111827]">{title}</span>.
        </p>
        <div className="flex items-center justify-center bg-[#F8F9FA] rounded-xl p-4">
          <QRCodeSVG value={url} size={192} includeMargin={false} level="M" />
        </div>
        <p className="text-[11px] text-[#9CA3AF] break-all">{url}</p>
      </div>
    </div>
  );
}

/**
 * One normalized milestone for the modal. `sequence` is the GoKart
 * sequence_number — a number for an ordered funnel step, null for an
 * independent / "whale" milestone (rendered with a star instead of a number).
 */
export interface OfferDetailMilestone {
  sequence: number | null;
  name: string;
  rewardDisplay: string;
}

/**
 * Source-agnostic shape the modal renders. Built from a DisplayOffer or a
 * DisplayGame via the adapters below, so one modal serves the Offers wall, the
 * GoKart Games, and Besitos games.
 */
export interface OfferDetailItem {
  title: string;
  bannerUrl: string | null;
  iconUrl: string | null;
  headlineReward: number;
  description: string;
  keyPoints: string[];
  rating: number | null;
  category: string | null;
  device?: string;      // raw platform ('ios' | 'android' | 'all') for the brand glyph
  deviceLabel: string;  // text label, e.g. 'iOS only' (no emoji)
  milestones: OfferDetailMilestone[];
  /** Static destination when one exists (Besitos); null for GoKart (the CTA
   *  resolves the URL per-click via the click handshake — see onCta). */
  ctaUrl: string | null;
}

function deviceLabelFor(device: string | undefined): string {
  const d = (device ?? '').toLowerCase();
  if (d === 'ios') return 'iOS only';
  if (d === 'android') return 'Android only';
  if (d === 'all') return 'All devices';
  return '';
}

/** DisplayOffer → normalized modal item. */
export function offerToDetailItem(offer: DisplayOffer): OfferDetailItem {
  return {
    title: offer.title,
    bannerUrl: offer.bannerUrl ?? null,
    iconUrl: offer.icon_url || null,
    headlineReward: offer.max_points,
    description: offer.description,
    keyPoints: offer.keyPoints ?? [],
    rating: offer.rating ?? null,
    category: offer.category,
    device: undefined, // offers don't carry a per-offer device on the wall contract
    deviceLabel: '',
    milestones: offer.events.map((e) => ({
      sequence: e.sequence ?? null,
      name: e.name,
      rewardDisplay: `+${e.points} pts`,
    })),
    ctaUrl: null, // GoKart — resolved per-click via the handshake
  };
}

/** DisplayGame → normalized modal item (GoKart or Besitos). */
export function gameToDetailItem(game: DisplayGame): OfferDetailItem {
  const isGokart = game.source === 'gokart';
  return {
    title: game.title,
    bannerUrl: game.bannerUrl ?? null,
    iconUrl: game.imageUrl || null,
    headlineReward: game.maxPoints,
    description: game.description,
    keyPoints: game.keyPoints ?? [],
    rating: game.rating ?? null,
    category: game.category,
    device: game.device,
    deviceLabel: deviceLabelFor(game.device),
    milestones: game.goals.map((g) => ({
      sequence: g.sequence ?? null,
      name: g.text,
      rewardDisplay: `+${g.points} pts`,
    })),
    // GoKart games resolve per-click; Besitos carry a static redirectUrl.
    ctaUrl: isGokart ? null : (game.redirectUrl || null),
  };
}

const MILESTONE_LIMIT = 5;

/**
 * Shared offer/game detail modal. Presentational — the terminal CTA calls
 * `onCta()`; the page owns the open path (GoKart handshake or Besitos static
 * URL). Matches the existing games-modal visual language.
 */
export default function OfferDetailModal({
  item,
  onClose,
  onCta,
}: {
  item: OfferDetailItem;
  onClose: () => void;
  onCta: () => void;
}) {
  const [showAll, setShowAll] = useState(false);

  const heroUrl = item.bannerUrl ?? item.iconUrl;
  const visible = showAll ? item.milestones : item.milestones.slice(0, MILESTONE_LIMIT);
  const hasMore = item.milestones.length > MILESTONE_LIMIT;

  // Running step counter so ordered milestones number 1..N and independent
  // (null-sequence) ones render with a star instead.
  let step = 0;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm" onClick={onClose}>
      <div
        className="bg-white rounded-2xl shadow-xl w-full max-w-lg mx-4 max-h-[85vh] flex flex-col"
        onClick={(e) => e.stopPropagation()}
        data-testid="offer-detail-modal"
      >
        <div className="overflow-y-auto flex-1">
          {/* Hero — banner if available, else icon on a branded gradient. */}
          <div className="relative h-48 rounded-t-2xl overflow-hidden bg-gradient-to-br from-[#1e1b4b] to-[#312e81]">
            {heroUrl ? (
              item.bannerUrl ? (
                <img src={heroUrl} alt="" className="w-full h-full object-cover" />
              ) : (
                <div className="w-full h-full flex items-center justify-center">
                  <img src={heroUrl} alt="" className="w-24 h-24 rounded-3xl object-cover shadow-lg" />
                </div>
              )
            ) : null}
            <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
            <button
              onClick={onClose}
              aria-label="Close"
              className="absolute top-3 right-3 text-white/80 hover:text-white bg-black/20 rounded-full p-1.5"
            >
              <X className="w-5 h-5" />
            </button>
            <div className="absolute bottom-4 left-4 right-4">
              <h2 className="text-xl font-bold font-display text-white">{item.title}</h2>
              <div className="flex items-center gap-2 mt-1 flex-wrap">
                {item.category && (
                  <span className="text-xs font-medium bg-white/20 text-white px-2 py-0.5 rounded-full">{item.category}</span>
                )}
                {item.rating != null && (
                  <span className="inline-flex items-center gap-1 text-xs font-medium bg-white/20 text-white px-2 py-0.5 rounded-full">
                    <Star className="w-3 h-3 fill-current" /> {item.rating.toFixed(1)}
                  </span>
                )}
              </div>
            </div>
          </div>

          <div className="p-6 space-y-5">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="bg-[#10B981]/10 text-[#10B981] text-sm font-bold px-3 py-1.5 rounded-full inline-flex items-center gap-1">
                <Coins className="w-3.5 h-3.5" /> Up to {item.headlineReward} pts
              </span>
              {item.deviceLabel && (
                <span className="text-xs text-[#6B7280]"><PlatformTag platform={item.device} label={item.deviceLabel} /></span>
              )}
            </div>

            {item.description && (
              <p className="text-sm text-[#6B7280] leading-relaxed whitespace-pre-line">{item.description}</p>
            )}

            {item.keyPoints.length > 0 && (
              <ul className="space-y-1.5" data-testid="offer-keypoints">
                {item.keyPoints.map((point, i) => (
                  <li key={i} className="flex items-start gap-2 text-sm text-[#374151]">
                    <Check className="w-4 h-4 text-[#10B981] flex-shrink-0 mt-0.5" />
                    <span>{point}</span>
                  </li>
                ))}
              </ul>
            )}

            {item.milestones.length > 0 && (
              <div>
                <h3 className="text-sm font-bold font-display text-[#111827] mb-2">Milestones</h3>
                <div className="space-y-2">
                  {visible.map((m, i) => {
                    const independent = m.sequence == null;
                    if (!independent) step += 1;
                    const badgeNumber = step;
                    return (
                      <div
                        key={`${m.name}-${i}`}
                        className="flex items-center justify-between rounded-xl px-4 py-3 bg-[#F8F9FA]"
                      >
                        <div className="flex items-center gap-3 min-w-0">
                          {independent ? (
                            <Star className="w-5 h-5 text-primary flex-shrink-0" />
                          ) : (
                            <span className="w-6 h-6 rounded-full bg-primary/10 text-primary text-xs font-bold flex items-center justify-center flex-shrink-0">
                              {badgeNumber}
                            </span>
                          )}
                          <span className="text-sm text-[#111827] truncate">{m.name}</span>
                        </div>
                        <span className="text-sm font-bold flex-shrink-0 text-[#10B981]">{m.rewardDisplay}</span>
                      </div>
                    );
                  })}
                </div>
                {hasMore && !showAll && (
                  <button
                    onClick={() => setShowAll(true)}
                    className="mt-2 text-sm text-primary font-medium hover:underline"
                  >
                    Show all {item.milestones.length} milestones
                  </button>
                )}
              </div>
            )}
          </div>
        </div>

        <div className="sticky bottom-0 bg-white border-t border-[#E5E7EB] p-4 rounded-b-2xl shadow-[0_-4px_12px_rgba(0,0,0,0.05)]">
          <button
            type="button"
            onClick={onCta}
            className="w-full inline-flex items-center justify-center gap-1.5 py-3 rounded-full bg-primary text-white text-sm font-semibold hover:bg-primary-dark transition-colors"
          >
            Start Earning <ExternalLink className="w-4 h-4" />
          </button>
        </div>
      </div>
    </div>
  );
}
